import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Subscription } from 'rxjs';

import { UserIdService } from './user-id.service';
import { GroupInvitesService } from './group-invites.service';
import { GroupInvite } from '../models/group-invite.model';
import { PendingGroupInvitesModalComponent } from '../components/pending-group-invites-modal/pending-group-invites-modal.component';

@Injectable({
  providedIn: 'root'
})
export class PendingGroupInvitesPromptService {
  private hasInitialised = false;
  private subscription?: Subscription;
  private currentUserId?: string;
  private promptedUsers = new Set<string>();
  private modalRef?: NgbModalRef;

  constructor(
    private auth: Auth,
    private userIdService: UserIdService,
    private groupInvitesService: GroupInvitesService,
    private modalService: NgbModal,
  ) { }

  init() {
    if (this.hasInitialised) {
      return;
    }

    this.hasInitialised = true;
    this.subscription = this.userIdService.get$().subscribe(userId => {
      if (!userId) {
        this.currentUserId = undefined;
        this.promptedUsers.clear();
        this.modalRef?.close();
        this.modalRef = undefined;
        return;
      }

      if (this.currentUserId !== userId) {
        this.currentUserId = userId;
      }

      if (this.promptedUsers.has(userId) || this.modalRef) {
        return;
      }

      void this.promptForPendingInvites(userId);
    });
  }

  private async promptForPendingInvites(userId: string) {
    const isReady = await this.waitForAuthenticatedUser(userId);

    if (!isReady) {
      return;
    }

    const invites = await firstValueFrom(this.groupInvitesService.getPendingGroupInvites$())
      .catch((error) => {
        console.error('Failed to load pending group invites', error);
        return null;
      });

    if (!invites) {
      return;
    }

    if (this.currentUserId !== userId || this.modalRef) {
      return;
    }

    this.promptedUsers.add(userId);

    if (!invites.length) {
      return;
    }

    const ref = this.modalService.open(PendingGroupInvitesModalComponent, {
      centered: true,
      size: 'lg',
      scrollable: true,
    });

    ref.componentInstance.invites = invites;
    this.modalRef = ref;

    ref.result.finally(() => {
      if (this.modalRef === ref) {
        this.modalRef = undefined;
      }
    });
  }

  private async waitForAuthenticatedUser(userId: string): Promise<boolean> {
    const existingUser = this.auth.currentUser;

    if (existingUser?.uid === userId) {
      await existingUser.getIdToken();
      return true;
    }

    const authUser = await new Promise<User | null>((resolve) => {
      let isFinished = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let unsubscribe: (() => void) | undefined;

      const finish = (user: User | null) => {
        if (isFinished) {
          return;
        }

        isFinished = true;

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (unsubscribe) {
          unsubscribe();
        }
        resolve(user);
      };

      timeoutId = setTimeout(() => finish(this.auth.currentUser), 3000);
      unsubscribe = onAuthStateChanged(
        this.auth,
        user => finish(user),
        () => finish(null),
      );

      if (isFinished && unsubscribe) {
        unsubscribe();
      }
    });

    if (authUser?.uid !== userId) {
      return false;
    }

    await authUser.getIdToken();
    return true;
  }
}
